/**
 * @fileoverview Pre-Checkout Signup Page - Account Creation Before Payment
 * @module PreCheckoutSignupPage
 * 
 * Business Context:
 * - Critical conversion step for account creation before Stripe checkout
 * - Supports two paths: direct CTA access (no property data) and funnel completion (with property data)
 * - Creates user account then redirects to Stripe checkout
 * - Property collection happens post-payment on dashboard if not provided
 * 
 * Conversion Psychology:
 * - Shows estimated savings when funnel data available (psychological anchor)
 * - Shows value proposition when accessed directly (from nav/bottom CTAs)
 * - Single-page form reduces friction
 * - Trust badges and price point reinforce value
 * - Account creation increases commitment before payment
 * 
 * Technical Context:
 * - Optionally reads URL params from landing page funnel
 * - Uses AuthContext register() then Stripe checkout
 * - Redirects to dashboard after successful payment
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams, useLocation, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { 
  Eye, 
  EyeOff, 
  Mail, 
  Lock, 
  AlertCircle, 
  CheckCircle,
  Shield,
  MapPin,
  ArrowRight,
  Sparkles
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useStripe } from '../hooks/useStripe';
import { apiClient } from '../api/client';
import { GoogleSignInButton } from '../components/auth/GoogleSignInButton';
import { 
  PROPERTY_VALUE_RANGES, 
  HOME_AGE_RANGES,
  PropertyValueRange,
  HomeAgeRange
} from '../components/home/PropertyDetailsForm';

// ================================
// Types & Validation
// ================================

interface SignupFormData {
  email: string;
  password: string;
  confirmPassword: string;
}

const schema = yup.object({
  email: yup
    .string()
    .email('Please enter a valid email address')
    .required('Email is required'),
  password: yup
    .string()
    .min(8, 'Password must be at least 8 characters')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
      'Password must contain uppercase, lowercase, and a number'
    )
    .required('Password is required'),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref('password')], 'Passwords must match')
    .required('Please confirm your password'),
});

// ================================
// Savings Calculation (matches SavingsCalculator)
// ================================

const TX_AVG_TAX_RATE = 0.022;
const BASE_SAVINGS_RATE = 0.12;
const MAX_SAVINGS_RATE = 0.25;

interface SavingsEstimate {
  lowEstimate: number;
  highEstimate: number;
}

interface SignupLocationState {
  oauthError?: string;
}

const calculateSavings = (
  valueRange: PropertyValueRange,
  ageRange: HomeAgeRange
): SavingsEstimate => {
  const estimatedTaxes = valueRange.midpoint * TX_AVG_TAX_RATE;
  const savingsRate = BASE_SAVINGS_RATE + ageRange.savingsBonus;
  const lowSavingsRate = Math.max(savingsRate - 0.05, 0.08);
  const highSavingsRate = Math.min(savingsRate + 0.08, MAX_SAVINGS_RATE);
  
  return {
    lowEstimate: Math.round(estimatedTaxes * lowSavingsRate),
    highEstimate: Math.round(estimatedTaxes * highSavingsRate),
  };
};

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(amount);
};

// ================================
// Main Component
// ================================

const PreCheckoutSignupPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { register: registerUser, isAuthenticated, user } = useAuth();
  const { createCheckoutSession, loading: stripeLoading, error: stripeError } = useStripe();
  
  // Form state
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isRegistrationFlow, setIsRegistrationFlow] = useState(false); // Prevents useEffect race condition

  // Parse URL params from landing page funnel
  const funnelData = useMemo(() => {
    const address = searchParams.get('address') || '';
    const streetAddress = searchParams.get('streetAddress') || '';
    const city = searchParams.get('city') || '';
    const county = searchParams.get('county') || '';
    const zip = searchParams.get('zip') || '';
    const valueId = searchParams.get('value') || '';
    const ageId = searchParams.get('age') || '';
    
    // Find the matching range objects
    const valueRange = PROPERTY_VALUE_RANGES.find(r => r.id === valueId) || null;
    const ageRange = HOME_AGE_RANGES.find(r => r.id === ageId) || null;
    
    // Calculate savings if we have both ranges
    let savings: SavingsEstimate | null = null;
    if (valueRange && ageRange) {
      savings = calculateSavings(valueRange, ageRange);
    }
    
    return {
      address,
      streetAddress,
      city,
      county,
      zip,
      valueRange,
      ageRange,
      savings,
    };
  }, [searchParams]);

  const signupReturnPath = useMemo(() => {
    const queryString = searchParams.toString();
    return queryString ? `/signup?${queryString}` : '/signup';
  }, [searchParams]);

  // React Hook Form setup
  const {
    register,
    handleSubmit,
    formState: { errors, isValid, touchedFields },
  } = useForm<SignupFormData>({
    resolver: yupResolver(schema),
    mode: 'onChange',
  });

  // Helper: Save property from funnel data if available
  const savePropertyFromFunnel = async () => {
    // Debug: Log what funnel data we have
    console.log('🏠 [savePropertyFromFunnel] Starting with funnel data:', {
      streetAddress: funnelData.streetAddress,
      city: funnelData.city,
      zip: funnelData.zip,
      county: funnelData.county,
      fullAddress: funnelData.address,
    });
    
    // Require at minimum: streetAddress and county (zip can be optional)
    if (!funnelData.streetAddress || !funnelData.county) {
      console.log('⚠️ [savePropertyFromFunnel] Missing required fields for property save (need streetAddress and county)');
      return;
    }
    
    try {
      // Look up county ID from county name
      console.log('🔍 [savePropertyFromFunnel] Looking up county:', funnelData.county);
      const countyResponse = await apiClient.properties.searchCounties(funnelData.county);
      console.log('📍 [savePropertyFromFunnel] County search response:', {
        status: countyResponse.status,
        success: countyResponse.data?.success,
        dataType: typeof countyResponse.data?.data,
        dataLength: Array.isArray(countyResponse.data?.data) ? countyResponse.data.data.length : 'N/A',
        fullResponse: countyResponse.data,
      });
      
      // Fix: Response structure is { success: true, data: counties[] }
      // After axios wraps it: countyResponse.data = { success: true, data: counties[] }
      const counties = countyResponse.data?.data || [];
      console.log('📋 [savePropertyFromFunnel] Parsed counties array:', counties);
      
      const matchedCounty = counties.find(
        (c: { name: string }) => c.name.toLowerCase() === funnelData.county.toLowerCase()
      );
      
      if (!matchedCounty) {
        console.warn('⚠️ [savePropertyFromFunnel] Could not find county:', funnelData.county, 'in counties list:', counties.map((c: { name: string }) => c.name));
        return;
      }
      
      console.log('✅ [savePropertyFromFunnel] Found county:', matchedCounty);
      
      // Save the property to user's account
      const propertyData = {
        addressLine1: funnelData.streetAddress,
        city: funnelData.city || 'Unknown',
        zipCode: funnelData.zip || '00000', // Default if not available
        countyId: matchedCounty.id,
        propertyType: 'single_family' as const, // Default type - can be updated later
        homesteadStatus: true, // Assume homestead for Texas
      };
      
      console.log('💾 [savePropertyFromFunnel] Saving property:', propertyData);
      const saveResponse = await apiClient.users.addProperty(propertyData);
      console.log('✅ [savePropertyFromFunnel] Property saved successfully:', saveResponse.data);
      
    } catch (propertyError) {
      // Don't block checkout if property save fails - user can add it later
      console.error('❌ [savePropertyFromFunnel] Could not save property from funnel data:', propertyError);
      // Log more details about the error
      if (propertyError instanceof Error) {
        console.error('❌ [savePropertyFromFunnel] Error details:', {
          message: propertyError.message,
          name: propertyError.name,
          stack: propertyError.stack,
        });
      }
    }
  };

  const alreadyPurchased = Boolean(user?.createdViaPurchase);

  // Handle checkout (called after registration or if already authenticated)
  const handleCheckout = async () => {
    try {
      await createCheckoutSession({
        guestCheckout: false, // Authenticated checkout
        successUrl: `${window.location.origin}/payment/success`,
        cancelUrl: `${window.location.origin}/signup?${searchParams.toString()}&payment=cancelled`,
      });
    } catch (error) {
      const checkoutError = error as Error & { code?: string };
      const alreadyOwned = checkoutError.code === 'ALREADY_PURCHASED' || checkoutError.message.toLowerCase().includes('already owns');
      if (alreadyOwned) {
        setSubmitError('You already have access. Redirecting to your dashboard...');
        setIsSubmitting(false);
        navigate('/dashboard');
        return;
      }
      console.error('Checkout error:', error);
      setSubmitError('Failed to start checkout. Please try again.');
      setIsSubmitting(false);
    }
  };

  // Handle already-authenticated users arriving at signup (not during registration)
  useEffect(() => {
    if (isAuthenticated && !isRegistrationFlow) {
      if (alreadyPurchased) {
        navigate('/dashboard');
        return;
      }
      // User arrived already logged in - save property then go to checkout
      console.log('👤 User already authenticated, proceeding to checkout...');
      const handleAuthenticatedUser = async () => {
        await savePropertyFromFunnel();
        handleCheckout();
      };
      handleAuthenticatedUser();
    }
  }, [isAuthenticated, isRegistrationFlow, alreadyPurchased, navigate]);

  // Handle form submission
  const onSubmit = async (data: SignupFormData) => {
    setIsSubmitting(true);
    setSubmitError(null);
    setIsRegistrationFlow(true); // Prevent useEffect from racing

    try {
      console.log('📝 Starting registration flow...');
      
      // Step 1: Register the user (name can be added later in profile)
      await registerUser(data.email, data.password);
      console.log('✅ Registration complete');
      
      // Step 2: Save property from funnel data (if available)
      // Must complete BEFORE checkout redirect
      await savePropertyFromFunnel();
      
      // Step 3: Trigger Stripe checkout (will redirect)
      console.log('💳 Starting checkout...');
      await handleCheckout();
      
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Registration failed';
      setSubmitError(message);
      setIsSubmitting(false);
      setIsRegistrationFlow(false);
    }
  };

  // Check if we have funnel data (from savings estimate flow)
  const hasFunnelData = Boolean(funnelData.valueRange && funnelData.ageRange);

  useEffect(() => {
    const locationState = location.state as SignupLocationState | null;
    if (!locationState?.oauthError) {
      return;
    }

    setSubmitError(locationState.oauthError);
    navigate(signupReturnPath, { replace: true });
  }, [location.state, navigate, signupReturnPath]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-teal-50/30" style={{ fontFamily: "'Plus Jakarta Sans', Inter, sans-serif" }}>
      {/* Navigation - Matching LandingPage style */}
      <nav className="sticky top-0 w-full bg-white/98 backdrop-blur-md z-50 border-b border-gray-100/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 sm:h-[4.25rem]">
            {/* Logo - Premium brand treatment matching LandingPage */}
            <Link to="/" className="flex items-center sm:items-end gap-2 sm:gap-2.5 group cursor-pointer select-none">
              <img 
                src="/logo-104.png" 
                alt="" 
                className="w-8 h-8 sm:w-[34px] sm:h-[34px] object-contain transition-transform duration-200 group-hover:scale-105 sm:mb-[2px]"
              />
              <div className="flex flex-col items-start">
                <span 
                  className="text-[0.95rem] sm:text-[1rem] leading-[1.2] tracking-[-0.015em] text-gray-900"
                  style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                >
                  <span className="font-semibold">Appeal Your</span>
                  {' '}
                  <span className="font-extrabold text-teal-600">Home</span>
                </span>
                <span 
                  className="hidden sm:block text-[0.54rem] font-semibold uppercase tracking-[0.08em] text-gray-400"
                  style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", marginTop: '1px' }}
                >
                  Learn. Advocate. Save.
                </span>
              </div>
            </Link>
            
            <Link 
              to="/auth" 
              className="text-gray-600 hover:text-teal-600 font-medium transition-colors text-sm sm:text-base"
              style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
            >
              <span className="sm:hidden">Sign In</span>
              <span className="hidden sm:inline">Already have an account? Sign In</span>
            </Link>
          </div>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-4 py-3 sm:py-8 lg:py-12 min-h-[calc(100dvh-4rem)] sm:min-h-[calc(100dvh-4.25rem)] lg:min-h-0 flex items-center lg:block">
        <div className="grid w-full lg:grid-cols-2 gap-6 lg:gap-12 items-start">
          
          {/* Left Column: Savings Summary & Value Props (desktop only) */}
          <div className="hidden lg:block lg:sticky lg:top-8">
            {/* Conditional: Savings Card (funnel data) or Value Prop Card (direct access) */}
            {hasFunnelData ? (
              /* Savings Card - When user came through funnel */
              <div className="bg-gradient-to-br from-teal-500 via-teal-600 to-emerald-600 rounded-2xl p-8 text-white shadow-2xl relative overflow-hidden mb-8">
                {/* Decorative elements */}
                <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -translate-y-20 translate-x-20" />
                <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full translate-y-16 -translate-x-16" />
                
                <div className="relative z-10">
                  {/* Address Badge */}
                  {funnelData.address && (
                    <div className="mb-4">
                      <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/20 backdrop-blur rounded-full text-sm font-medium">
                        <MapPin className="w-4 h-4" />
                        {funnelData.address}
                      </span>
                    </div>
                  )}

                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="w-5 h-5 text-yellow-300" />
                    <span className="text-sm font-semibold text-white/90 uppercase tracking-wider">
                      Your Estimated Savings
                    </span>
                  </div>

                  {/* Savings Display */}
                  <div className="mb-6">
                    <div className="flex items-baseline gap-2 mb-1">
                      <span className="text-4xl md:text-5xl font-black tracking-tight">
                        {formatCurrency(funnelData.savings?.lowEstimate || 0)}
                      </span>
                      <span className="text-xl text-white/60 font-light">to</span>
                      <span className="text-4xl md:text-5xl font-black tracking-tight text-yellow-300">
                        {formatCurrency(funnelData.savings?.highEstimate || 0)}
                      </span>
                    </div>
                    <p className="text-white/80 text-lg">
                      estimated annual savings
                    </p>
                  </div>

                  {/* Property Summary */}
                  <div className="bg-white/10 backdrop-blur rounded-xl p-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-white/70">Property Value:</span>
                      <span className="font-semibold">{funnelData.valueRange?.label}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-white/70">Home Age:</span>
                      <span className="font-semibold">{funnelData.ageRange?.label}</span>
                    </div>
                    {funnelData.county && (
                      <div className="flex justify-between text-sm">
                        <span className="text-white/70">County:</span>
                        <span className="font-semibold">{funnelData.county}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              /* Value Proposition Card - When user came directly from CTAs */
              <div className="bg-gradient-to-br from-teal-500 via-teal-600 to-emerald-600 rounded-2xl p-8 text-white shadow-2xl relative overflow-hidden mb-8">
                {/* Decorative elements */}
                <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -translate-y-20 translate-x-20" />
                <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full translate-y-16 -translate-x-16" />
                
                <div className="relative z-10">
                  <div className="flex items-center gap-2 mb-4">
                    <Sparkles className="w-6 h-6 text-yellow-300" />
                    <span className="text-sm font-semibold text-white/90 uppercase tracking-wider">
                      Texas Property Tax Appeal System
                    </span>
                  </div>

                  <h2 className="text-3xl md:text-4xl font-black tracking-tight mb-4">
                    Save <span className="text-yellow-300">$250 – $2,500+</span> on Your Property Taxes
                  </h2>
                  
                  <p className="text-white/80 text-lg mb-6">
                    Join thousands of Texas homeowners who have successfully lowered their property tax bills using our proven 4-step system.
                  </p>

                  {/* Quick Stats */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white/10 backdrop-blur rounded-xl p-4 text-center">
                      <div className="text-2xl font-bold text-yellow-300">95%</div>
                      <div className="text-sm text-white/70">Success Rate</div>
                    </div>
                    <div className="bg-white/10 backdrop-blur rounded-xl p-4 text-center">
                      <div className="text-2xl font-bold text-yellow-300">4.8/5</div>
                      <div className="text-sm text-white/70">Customer Rating</div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Trust Badges */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">What You're Getting:</h3>
              
              <div className="space-y-3">
                {[
                  { text: 'Complete 4-Step Video Course', value: '$197' },
                  { text: 'AI Argument Generator', value: '$297' },
                  { text: 'Interactive Maintenance Checklists', value: '$97' },
                  { text: 'Evidence Analysis Toolkit', value: '$97' },
                  { text: '30-Day Email Support', value: '$29', bonus: true },
                ].map((item, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CheckCircle className={`w-5 h-5 ${item.bonus ? 'text-yellow-500' : 'text-teal-500'}`} />
                      <span className="text-gray-700">
                        {item.bonus && <span className="text-yellow-600 font-semibold mr-1">BONUS:</span>}
                        {item.text}
                      </span>
                    </div>
                    <span className="text-gray-400 line-through text-sm">{item.value}</span>
                  </div>
                ))}
              </div>

              <div className="mt-6 pt-6 border-t border-gray-100 text-center">
                <p className="text-gray-500 text-sm mb-1">Total Value: <span className="line-through">$814</span></p>
                <p className="text-3xl font-extrabold text-teal-600">Today: $149</p>
              </div>

              <div className="mt-4 flex items-center justify-center gap-2 text-sm text-gray-500">
                <Shield className="w-4 h-4 text-green-500" />
                95%+ Success Rate • 4.8/5 Rating
              </div>
            </div>
          </div>

          {/* Right Column: Registration Form */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-4 sm:p-8">
            <div className="text-center mb-4 sm:mb-8">
              <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-teal-600 mb-1.5 lg:hidden">Step 1 of 2</p>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Create Your Account
              </h1>
              <p className="max-w-sm mx-auto text-sm sm:text-base leading-6 text-gray-600">
                Create your account to continue to checkout.
              </p>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-3.5 sm:space-y-5">
              {/* Google Sign-In Button */}
              <GoogleSignInButton
                disabled={isSubmitting || stripeLoading}
                returnPath={signupReturnPath}
                className="py-2.5 sm:py-3.5 px-6 border-2 border-gray-200 rounded-xl text-[15px] sm:text-base font-semibold hover:border-gray-300"
              />

              {/* Divider */}
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200"></div>
                </div>
                <div className="relative flex justify-center text-[13px] sm:text-sm">
                  <span className="px-4 bg-white text-gray-500">or continue with email</span>
                </div>
              </div>

              {/* Email Field */}
              <div className="space-y-1.5">
                <label htmlFor="email" className="text-sm font-medium text-gray-700">
                  Email address
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    {...register('email')}
                    type="email"
                    id="email"
                    autoComplete="email"
                    className={`
                      block w-full pl-10 pr-3 py-2.5 sm:py-3 border rounded-lg shadow-sm
                      placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2
                      transition-colors duration-200
                      ${
                        errors.email
                          ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                          : touchedFields.email && !errors.email
                          ? 'border-green-300 focus:border-green-500 focus:ring-green-500'
                          : 'border-gray-300 focus:border-teal-500 focus:ring-teal-500'
                      }
                    `}
                    placeholder="you@example.com"
                  />
                </div>
                {errors.email && (
                  <div className="flex items-center mt-1 text-sm text-red-600">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {errors.email.message}
                  </div>
                )}
              </div>

              {/* Password Field */}
              <div className="space-y-1.5">
                <label htmlFor="password" className="text-sm font-medium text-gray-700">
                  Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    {...register('password')}
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    autoComplete="new-password"
                    className={`
                      block w-full pl-10 pr-12 py-2.5 sm:py-3 border rounded-lg shadow-sm
                      placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2
                      transition-colors duration-200
                      ${
                        errors.password
                          ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                          : touchedFields.password && !errors.password
                          ? 'border-green-300 focus:border-green-500 focus:ring-green-500'
                          : 'border-gray-300 focus:border-teal-500 focus:ring-teal-500'
                      }
                    `}
                    placeholder="Create a password"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <div className="flex items-start mt-1 text-sm text-red-600">
                    <AlertCircle className="h-4 w-4 mr-1 mt-0.5 flex-shrink-0" />
                    <span>{errors.password.message}</span>
                  </div>
                )}
              </div>

              {/* Confirm Password Field */}
              <div className="space-y-1.5">
                <label htmlFor="confirmPassword" className="text-sm font-medium text-gray-700">
                  Confirm password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    {...register('confirmPassword')}
                    type={showConfirmPassword ? 'text' : 'password'}
                    id="confirmPassword"
                    autoComplete="new-password"
                    className={`
                      block w-full pl-10 pr-12 py-2.5 sm:py-3 border rounded-lg shadow-sm
                      placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2
                      transition-colors duration-200
                      ${
                        errors.confirmPassword
                          ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                          : touchedFields.confirmPassword && !errors.confirmPassword
                          ? 'border-green-300 focus:border-green-500 focus:ring-green-500'
                          : 'border-gray-300 focus:border-teal-500 focus:ring-teal-500'
                      }
                    `}
                    placeholder="Confirm your password"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                    )}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <div className="flex items-center mt-1 text-sm text-red-600">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {errors.confirmPassword.message}
                  </div>
                )}
              </div>

              {/* Error Messages */}
              {(submitError || stripeError) && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
                    <span className="text-sm text-red-800">{submitError || stripeError}</span>
                  </div>
                </div>
              )}

              {/* Payment cancelled message */}
              {searchParams.get('payment') === 'cancelled' && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <AlertCircle className="h-5 w-5 text-amber-600 mr-2" />
                    <span className="text-sm text-amber-800">
                      Payment was cancelled. Ready to try again when you are!
                    </span>
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={!isValid || isSubmitting || stripeLoading}
                className={`
                  relative w-full flex justify-center items-center py-3.5 px-6 border border-transparent rounded-xl
                  text-base sm:text-lg font-bold transition-all duration-200 transform
                  ${
                    !isValid || isSubmitting || stripeLoading
                      ? 'bg-gray-400 cursor-not-allowed text-white'
                      : 'bg-yellow-400 hover:bg-yellow-500 text-gray-900 shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]'
                  }
                `}
              >
                {isSubmitting || stripeLoading ? (
                  <span className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    {isSubmitting ? 'Creating account...' : 'Redirecting to checkout...'}
                  </span>
                ) : (
                  <>
                    <span>Continue to checkout</span>
                    <ArrowRight className="absolute right-5 w-5 h-5" />
                  </>
                )}
              </button>

              {/* Mobile-friendly guarantee copy */}
              <div className="mt-3 text-center">
                <p className="inline-flex items-center justify-center gap-2 text-sm font-semibold text-gray-600">
                  <Shield className="w-4 h-4 text-green-500" />
                  Money-back guarantee
                </p>
                <p className="mt-1 text-xs leading-5 text-gray-500">
                  Refund if you don&apos;t save at least $150.
                </p>
              </div>

              {/* Terms */}
              <p className="text-[11px] leading-5 text-center text-gray-500 mt-3">
                By continuing, you agree to our{' '}
                <Link to="/terms" className="text-teal-600 hover:underline" target="_blank">Terms</Link>
                {' '}and{' '}
                <Link to="/privacy" className="text-teal-600 hover:underline" target="_blank">Privacy Policy</Link>.
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreCheckoutSignupPage;

